import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from '@/shared/api/axios';
import { clearSession } from './userSlice';

// 取得通知列表（依當前選中偶像 idol_id 過濾）
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async ({ page = 1, limit = 10, isLoadMore = false } = {}, { getState, rejectWithValue }) => {
    try {
      const idolId = getState().user.idolId;
      const response = await Axios.post('/notify/get-list', {
        idol_id: idolId,
        is_read: null,
        page,
        limit,
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        return {
          notifications: response.data.data.notifications,
          total: response.data.data.total,
          unreadCount: response.data.data.unread_count,
          page,
          limit,
          isLoadMore,
        };
      } else {
        return rejectWithValue(response.data?.status_code);
      }
    } catch (error) {
      // axios 攔截器保證每個被 reject 的請求都帶 httpErrorCode（HTTP_xxx / NETWORK_ERROR）
      return rejectWithValue(error.httpErrorCode);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  hasMore: true,
  isLoading: false,
};

export const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    prependNotification(state, action) {
      // WebSocket 收到新通知時，加到最前面
      state.notifications = [action.payload, ...state.notifications];
      state.unreadCount += 1;
      state.total += 1;
    },
    markAsRead(state, action) {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead(state) {
      state.notifications = state.notifications.map(n => ({ ...n, is_read: true }));
      state.unreadCount = 0;
    },
    resetNotifications(state) {
      state.notifications = [];
      state.unreadCount = 0;
      state.total = 0;
      state.page = 1;
      state.hasMore = true;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { notifications, total, unreadCount, page, limit, isLoadMore } = action.payload;
        state.isLoading = false;
        state.notifications = isLoadMore
          ? [...state.notifications, ...notifications]
          : notifications;
        state.total = total;
        state.unreadCount = unreadCount;
        state.page = page;
        state.hasMore = notifications.length === limit;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
      })
      // 登出／帳號停權／SYSTEM_1004／後台重導時清空：通知會持久化在 localStorage，不清會留給下一個使用同一台裝置的人
      .addCase(clearSession, () => initialState);
  },
});

export const {
  prependNotification,
  markAsRead,
  markAllAsRead,
  resetNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
